import React, { useState } from 'react';
import { InlineSVG } from '../../../../Components/Icons';
import { colors } from '../../../../globalStyles';
import { ComplexNeedsPill } from '../../../../Components/Beacon/ComplexNeedsPill';
import {
  CollapsedPhraseDiv,
  ContributionBadgeDiv,
  ContributionBodyDiv,
  ContributionHeaderDiv,
  ContributionPairDiv,
  EmptyStateDiv,
  ExpandedHighlightTr,
  ExpandedRowTr,
  FlagDetailCardDiv,
  FlagDetailHeadingDiv,
  FlagDetailTextDiv,
  FlagNameTd,
  FlagsHeaderDiv,
  FlagsTable,
  FlagsTableDiv,
  FlagsTitle,
  FlagToggleButton,
  SeverityTd,
  SortHeaderButton,
  SourceQuestionsUl,
} from './AssessmentFlags.styles';
import {
  PERSISTENCE_DIMENSION_LABELS,
  WELLBEING_DIMENSION_LABELS,
} from '../AssessmentIndex/AssessmentIndex.constants';
import { SaiStudentFlag } from '../saiStudentDetail.types';
import { ScoreContributionIcon } from './scoreContributionIcons';
import { humanizeEnumValue } from '../../../../utils/humanizeEnumValue';
import { getSeverityRank, getSeverityTone } from './severity';

const ScoreContributionCell = ({ flag }: { flag: SaiStudentFlag }) => {
  const { wellbeingDimensions, persistenceDimensions } = flag;
  if (!wellbeingDimensions.length && !persistenceDimensions.length) {
    return null;
  }

  return (
    <ContributionPairDiv>
      <ContributionBadgeDiv $index="wellbeing">
        <ContributionHeaderDiv $index="wellbeing">Wellbeing</ContributionHeaderDiv>
        <ContributionBodyDiv $index="wellbeing" $muted={!wellbeingDimensions.length}>
          {wellbeingDimensions.length
            ? wellbeingDimensions.map(dimension => (
                <React.Fragment key={dimension}>
                  <ScoreContributionIcon kind="wellbeing" dimension={dimension} />
                  {WELLBEING_DIMENSION_LABELS[dimension] ?? humanizeEnumValue(dimension)}
                </React.Fragment>
              ))
            : 'Not scored'}
        </ContributionBodyDiv>
      </ContributionBadgeDiv>
      <ContributionBadgeDiv $index="persistence">
        <ContributionHeaderDiv $index="persistence">Persistence</ContributionHeaderDiv>
        <ContributionBodyDiv $index="persistence" $muted={!persistenceDimensions.length}>
          {persistenceDimensions.length
            ? persistenceDimensions.map(dimension => (
                <React.Fragment key={dimension}>
                  <ScoreContributionIcon kind="persistence" dimension={dimension} />
                  {PERSISTENCE_DIMENSION_LABELS[dimension] ?? humanizeEnumValue(dimension)}
                </React.Fragment>
              ))
            : 'Not scored'}
        </ContributionBodyDiv>
      </ContributionBadgeDiv>
    </ContributionPairDiv>
  );
};

/** A flag opens only when the run served something to put behind the chevron. */
const hasDetail = (flag: SaiStudentFlag) =>
  !!flag.whatItMeans || flag.sourceQuestions.length > 0 || !!flag.sourceInstrument;

type SortField = 'name' | 'severity';

const severityRank = (flag: SaiStudentFlag) => getSeverityRank(flag.flagRiskScaleLevel);

export const AssessmentFlags = ({
  flags,
  hasComplexNeeds,
  minWidth = '860px',
}: {
  flags: SaiStudentFlag[];
  hasComplexNeeds: boolean;
  /** How wide the table insists on being. A caller in a narrower pane asks for less. */
  minWidth?: string;
}): React.ReactElement => {
  // Expansion is per row rather than per flag key, because one run can carry several rows sharing a
  // key and the contract does not promise otherwise.
  const [expandedFlagIds, setExpandedFlagIds] = useState<Set<number>>(new Set());
  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortDescending, setSortDescending] = useState(true);

  const toggleFlag = (flagId: number) => {
    setExpandedFlagIds(previous => {
      const next = new Set(previous);
      if (next.has(flagId)) {
        next.delete(flagId);
      } else {
        next.add(flagId);
      }
      return next;
    });
  };

  const toggleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDescending(previous => !previous);
    } else {
      setSortField(field);
      setSortDescending(field === 'severity');
    }
  };

  const displayFlags = [...flags].sort((first, second) => {
    // Both keys read ascending so one direction turns the sort the user asked to turn. A severity
    // tie always breaks by name ascending, whichever way severity itself is running.
    const direction = sortDescending ? -1 : 1;
    const byName = first.displayName.localeCompare(second.displayName);
    if (sortField === 'name') {
      return byName * direction;
    }
    const bySeverity = severityRank(first) - severityRank(second);
    return bySeverity * direction || byName;
  });

  const sortArrow = (field: SortField) =>
    sortField === field ? (sortDescending ? '↓' : '↑') : undefined;

  const ariaSort = (field: SortField) =>
    sortField === field ? (sortDescending ? 'descending' : 'ascending') : undefined;

  return (
    <div>
      <FlagsHeaderDiv>
        <FlagsTitle>Flagged Concerns</FlagsTitle>
        {hasComplexNeeds && <ComplexNeedsPill />}
      </FlagsHeaderDiv>
      <FlagsTableDiv>
        <FlagsTable $minWidth={minWidth}>
          <thead>
            <tr>
              <th aria-sort={ariaSort('name')}>
                <SortHeaderButton type="button" onClick={() => toggleSort('name')}>
                  Flag {sortArrow('name') && <span aria-hidden="true">{sortArrow('name')}</span>}
                </SortHeaderButton>
              </th>
              <th aria-sort={ariaSort('severity')}>
                <SortHeaderButton type="button" onClick={() => toggleSort('severity')}>
                  Severity{' '}
                  {sortArrow('severity') && <span aria-hidden="true">{sortArrow('severity')}</span>}
                </SortHeaderButton>
              </th>
              <th>Score Contribution</th>
            </tr>
          </thead>
          <tbody>
            {displayFlags.length ? (
              displayFlags.map(flag => {
                const expandable = hasDetail(flag);
                const expanded = expandedFlagIds.has(flag.id);
                const RowComponent = expanded ? ExpandedHighlightTr : 'tr';

                return (
                  <React.Fragment key={flag.id}>
                    <RowComponent>
                      <FlagNameTd>
                        {expandable ? (
                          <FlagToggleButton
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => toggleFlag(flag.id)}
                          >
                            <InlineSVG
                              aria-hidden="true"
                              icon={expanded ? 'chevron-down' : 'chevron-right'}
                              size={16}
                              fill={colors.text}
                            />
                            <div>
                              {flag.displayName}
                              {flag.collapsedPhrase && (
                                <CollapsedPhraseDiv>{flag.collapsedPhrase}</CollapsedPhraseDiv>
                              )}
                            </div>
                          </FlagToggleButton>
                        ) : (
                          flag.displayName
                        )}
                      </FlagNameTd>
                      <SeverityTd $tone={getSeverityTone(flag.flagRiskScaleLevel)}>
                        {flag.flagRiskScaleLevel}
                      </SeverityTd>
                      <td>
                        <ScoreContributionCell flag={flag} />
                      </td>
                    </RowComponent>
                    {expanded && expandable && (
                      <ExpandedRowTr>
                        <td colSpan={3}>
                          <FlagDetailCardDiv>
                            {flag.whatItMeans && (
                              <div>
                                <FlagDetailHeadingDiv>What It Means</FlagDetailHeadingDiv>
                                <FlagDetailTextDiv>{flag.whatItMeans}</FlagDetailTextDiv>
                              </div>
                            )}
                            {flag.sourceQuestions.length > 0 && (
                              <div>
                                <FlagDetailHeadingDiv>Source Questions</FlagDetailHeadingDiv>
                                <SourceQuestionsUl>
                                  {flag.sourceQuestions.map(question => (
                                    <li key={question}>{question}</li>
                                  ))}
                                </SourceQuestionsUl>
                              </div>
                            )}
                            {flag.sourceInstrument && (
                              <div>
                                <FlagDetailHeadingDiv>Scored From</FlagDetailHeadingDiv>
                                <FlagDetailTextDiv>{flag.sourceInstrument}</FlagDetailTextDiv>
                              </div>
                            )}
                          </FlagDetailCardDiv>
                        </td>
                      </ExpandedRowTr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={3}>
                  <EmptyStateDiv>This assessment raised no flags.</EmptyStateDiv>
                </td>
              </tr>
            )}
          </tbody>
        </FlagsTable>
      </FlagsTableDiv>
    </div>
  );
};
